import { useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const mxn = (v) => v.toLocaleString("es-MX", { maximumFractionDigits: 0 });

function Trend({ v }) {
  if (v === "sube") return <span className="text-emerald-600 flex items-center gap-0.5 text-xs"><TrendingUp className="w-3.5 h-3.5" /> Sube</span>;
  if (v === "baja") return <span className="text-destructive/80 flex items-center gap-0.5 text-xs"><TrendingDown className="w-3.5 h-3.5" /> Baja</span>;
  if (v === "estable") return <span className="text-navy/40 flex items-center gap-0.5 text-xs"><Minus className="w-3.5 h-3.5" /> Estable</span>;
  return null;
}

function KpiCard({ label, value, target, trend }) {
  const att = target ? Math.round((value / target) * 100) : null;
  const tone = att == null ? "violet" : att >= 100 ? "emerald" : att >= 70 ? "amber" : "destructive";
  const bar = { violet: "bg-violet", emerald: "bg-emerald-500", amber: "bg-amber-500", destructive: "bg-destructive" }[tone];
  return (
    <div className="card-surface p-4" data-testid={`kpi-card-${label}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs text-navy/50 truncate">{label}</div>
        {trend && <Trend v={trend} />}
      </div>
      <div className="mt-1 font-editorial text-3xl text-navy/90">{mxn(value)}</div>
      {target > 0 && (
        <>
          <div className="mt-1 text-xs text-navy/40">Meta {mxn(target)} · {att}%</div>
          <div className="mt-2 h-1.5 rounded-full bg-navy/10 overflow-hidden"><div className={`h-full rounded-full ${bar} transition-all`} style={{ width: `${Math.min(att, 100)}%` }} /></div>
        </>
      )}
    </div>
  );
}

function Gauge({ label, metrics, status }) {
  const badge = { optimo: "badge-emerald", advertencia: "badge-amber", critico: "badge-danger" }[status] || "badge-muted";
  const statusLabel = { optimo: "Óptimo", advertencia: "Advertencia", critico: "Crítico" }[status] || status;
  return (
    <div className="card-surface p-4 space-y-2" data-testid={`gauge-card-${label}`}>
      <div className="flex items-center justify-between gap-2"><div className="text-sm text-navy/80 truncate">{label}</div><span className={`badge ${badge}`}>{statusLabel}</span></div>
      {metrics.map(([k, lbl, val]) => {
        const tone = val >= 90 ? "bg-destructive" : val >= 70 ? "bg-amber-500" : "bg-emerald-500";
        return (
          <div key={k}>
            <div className="flex justify-between text-xs text-navy/50"><span>{lbl}</span><span>{val}%</span></div>
            <div className="h-1.5 rounded-full bg-navy/10 overflow-hidden"><div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(val, 100)}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

export default function ModuleDashboard({ cfg, items }) {
  const d = cfg.dashboard;
  const filterKeys = d.filters || [];
  const [active, setActive] = useState({});

  const fieldDef = (k) => cfg.fields.find((x) => x.key === k);
  const optLabel = (fd, v) => (Array.isArray(fd?.options) ? fd.options : []).find((o) => String(o.value) === String(v))?.label || v;
  const distinct = (k) => [...new Set(items.map((it) => it[k]).filter((v) => v != null && v !== ""))];

  const filtered = items.filter((it) => filterKeys.every((k) => !active[k] || String(it[k]) === active[k]));

  const FilterBar = () => filterKeys.length === 0 ? null : (
    <div className="flex flex-wrap gap-4" data-testid="dashboard-filters">
      {filterKeys.map((k) => {
        const fd = fieldDef(k);
        const vals = distinct(k);
        if (vals.length === 0) return null;
        return (
          <div key={k} className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-navy/40 mr-0.5">{fd?.label || k}:</span>
            <button onClick={() => setActive({ ...active, [k]: null })} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${!active[k] ? "border-violet bg-violet/5 text-violet" : "border-navy/10 text-navy/50 hover:border-navy/25"}`} data-testid={`filter-${k}-all`}>Todos</button>
            {vals.map((v) => (
              <button key={String(v)} onClick={() => setActive({ ...active, [k]: String(v) })} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active[k] === String(v) ? "border-violet bg-violet/5 text-violet" : "border-navy/10 text-navy/50 hover:border-navy/25"}`} data-testid={`filter-${k}-${v}`}>{optLabel(fd, v)}</button>
            ))}
          </div>
        );
      })}
    </div>
  );

  if (d.kind === "gauge") {
    return (
      <div className="space-y-4" data-testid="module-dashboard">
        <FilterBar />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((it) => (
            <Gauge key={it.id} label={it[d.label] || "—"} status={it[d.status]}
              metrics={d.metrics.map(([k, lbl]) => [k, lbl, num(it[k])])} />
          ))}
        </div>
      </div>
    );
  }
  // kpi / metric — summary strip + cards
  const values = filtered.map((it) => num(it[d.value]));
  const total = values.reduce((a, b) => a + b, 0);
  const avg = values.length ? Math.round(total / values.length) : 0;
  return (
    <div className="space-y-4" data-testid="module-dashboard">
      <FilterBar />
      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
        <div className="card-surface p-4"><div className="text-xs text-navy/50">Indicadores</div><div className="font-editorial text-3xl text-navy/90 mt-1" data-testid="dashboard-count">{filtered.length}</div></div>
        <div className="card-surface p-4"><div className="text-xs text-navy/50">Suma de valores</div><div className="font-editorial text-3xl text-navy/90 mt-1">{mxn(total)}</div></div>
        <div className="card-surface p-4"><div className="text-xs text-navy/50">Promedio</div><div className="font-editorial text-3xl text-navy/90 mt-1">{mxn(avg)}</div></div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((it) => (
          <KpiCard key={it.id} label={it[d.label] || "—"} value={num(it[d.value])}
            target={d.target ? num(it[d.target]) : 0} trend={d.trend ? it[d.trend] : null} />
        ))}
      </div>
    </div>
  );
}
